import { Controller } from "@hotwired/stimulus";

// Single controller for the membership migration screen.
//
// Two modes:
// 1. Manual (default): form is visible, user picks plan + fills fields.
// 2. Oscar-assisted: user clicks "Ask Oscar" → fetch recommendation → apply to form.
//    If a cached recommendation exists, it's rendered server-side and auto-applied on connect.
export default class extends Controller {
  static targets = [
    "planSelect",
    "recurringFields",
    "oneTimeFields",
    "creditPackFields",
    "billingDate",
    "overrideAmount",
    "endDate",
    "creditsRemaining",
    "makeFree",
    "enrollmentFee",
    "createPaused",
    "pausedUntil",
    // Pre-submit summary block (built by refreshSummary)
    "summary",
    // Ask Oscar UI
    "askOscarSection",
    "askOscarButton",
    "oscarLoading",
    "oscarResult",
    // Oscar data (hidden spans — from cached recommendation or fetch result)
    "oscarAutoApply",
    "oscarPlanId",
    "oscarNextBillingDate",
    "oscarEndDate",
    "oscarPriceOverride",
    "oscarCreditsRemaining",
    "oscarClassification",
  ];

  static values = {
    plans: Object,
    lastPayment: String,
    today: String,
    oscarUrl: String,
  };

  connect() {
    if (this.hasPlanSelectTarget) {
      this.syncFieldGroups(this.selectedPlanId());
    }
    // Auto-fire Oscar fetch if no cached recommendation was rendered server-side
    if (this.oscarUrlValue && !this.hasOscarAutoApplyTarget) {
      this.askOscar();
    }
    this.refreshSummary();
  }

  // Cached recommendation rendered inline — auto-apply on connect
  oscarAutoApplyTargetConnected() {
    this.applyOscar();
  }

  // ── Ask Oscar (on-demand fetch) ──

  async askOscar() {
    if (!this.oscarUrlValue) return;

    // Swap button for spinner
    if (this.hasAskOscarButtonTarget) this.askOscarButtonTarget.classList.add("hidden");
    if (this.hasOscarLoadingTarget) this.oscarLoadingTarget.classList.remove("hidden");

    try {
      const response = await fetch(this.oscarUrlValue, {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" }
      });
      const html = await response.text();

      // Inject the response HTML (contains the recommendation card with data targets)
      if (this.hasOscarResultTarget) {
        this.oscarResultTarget.innerHTML = html;
        this.oscarResultTarget.classList.remove("hidden");
      }
      // Hide the ask section entirely
      if (this.hasAskOscarSectionTarget) this.askOscarSectionTarget.classList.add("hidden");

      // The injected HTML won't auto-trigger targetConnected for oscar data spans
      // because they're added via innerHTML (not Turbo). Apply manually.
      this.applyOscarFromDOM();
    } catch (e) {
      // Show the button again on failure
      if (this.hasAskOscarButtonTarget) this.askOscarButtonTarget.classList.remove("hidden");
      if (this.hasOscarLoadingTarget) this.oscarLoadingTarget.classList.add("hidden");
    }
  }

  // Read Oscar data from the injected HTML (querySelector since targets won't auto-register from innerHTML)
  applyOscarFromDOM() {
    const container = this.hasOscarResultTarget ? this.oscarResultTarget : this.element;
    const read = (key) => {
      const el = container.querySelector(`[data-migration-form-target="${key}"]`);
      if (!el) return null;
      const val = el.dataset.value;
      return (val === undefined || val === "" || val === "null") ? null : val;
    };

    const planId = read("oscarPlanId");
    const nextBilling = read("oscarNextBillingDate");
    const endDate = read("oscarEndDate");
    const priceOverride = read("oscarPriceOverride");
    const credits = read("oscarCreditsRemaining");

    if (planId && this.hasPlanSelectTarget) {
      this.planSelectTarget.value = planId;
      this.syncFieldGroups(planId);
    }

    this.setTarget("billingDate", nextBilling);
    this.setTarget("overrideAmount", priceOverride);
    this.setTarget("endDate", endDate);
    this.setTarget("creditsRemaining", credits);
  }

  // ── Apply from Stimulus targets (cached recommendation rendered server-side) ──

  applyOscar() {
    const planId = this.readOscarTarget("oscarPlanId");
    const nextBilling = this.readOscarTarget("oscarNextBillingDate");
    const endDate = this.readOscarTarget("oscarEndDate");
    const priceOverride = this.readOscarTarget("oscarPriceOverride");
    const credits = this.readOscarTarget("oscarCreditsRemaining");

    if (planId && this.hasPlanSelectTarget) {
      this.planSelectTarget.value = planId;
      this.syncFieldGroups(planId);
    }

    this.setTarget("billingDate", nextBilling);
    this.setTarget("overrideAmount", priceOverride);
    this.setTarget("endDate", endDate);
    this.setTarget("creditsRemaining", credits);
  }

  // ── Plan change ──

  planChanged() {
    const planId = this.selectedPlanId();
    const plan = this.plansValue[planId];
    if (!plan) return;

    this.syncFieldGroups(planId);

    if (plan.type === "recurring") {
      this.suggestBillingDate(plan);
    }
    this.refreshSummary();
  }

  // ── Summary block ──
  // Updates the "Starts / Billing" review card above the submit button.
  // Wired to fire on every input/change inside the controller's element
  // so it stays in sync with plan choice, billing date, override, free
  // toggle, end date, and credits.
  refreshSummary() {
    if (!this.hasSummaryTarget) return;

    const plan = this.plansValue[this.selectedPlanId()];
    if (!plan) {
      this.summaryTarget.innerHTML = '<p class="text-sm text-gray-500 dark:text-gray-400 italic">Pick a plan to see the summary.</p>';
      return;
    }

    const starts = `<strong>Starts:</strong> Today (${this.formatDate(this.todayValue)})`;

    let billingHtml;
    if (this.isMakeFree()) {
      billingHtml = `<strong>Billing:</strong> <span class="text-emerald-700 dark:text-emerald-400">No charge — FREE membership</span>`;
    } else if (plan.type === "credit_pack") {
      const credits = this.readValue("creditsRemaining");
      const end = this.readValue("endDate");
      const credsPart = credits ? ` (${credits} credits${end ? ` · expires ${this.formatDate(end)}` : ""})` : "";
      billingHtml = `<strong>Billing:</strong> No recurring charge — credit pack${credsPart}`;
    } else if (plan.type === "one_time") {
      const end = this.readValue("endDate");
      const endPart = end ? ` · access ends ${this.formatDate(end)}` : "";
      billingHtml = `<strong>Billing:</strong> No recurring charge — prepaid${endPart}`;
    } else {
      // recurring
      const date = this.readValue("billingDate");
      const overrideRaw = this.readValue("overrideAmount");
      const overrideCents = this.parseDollarsToCents(overrideRaw);
      const cents = overrideCents != null ? overrideCents : plan.price_cents;
      const priceStr = this.formatMoney(cents);
      const cadence = this.formatInterval(plan);
      if (date) {
        billingHtml = `<strong>Billing:</strong> First charge ${this.formatDate(date)}, then ${priceStr} ${cadence}`;
      } else {
        billingHtml = `<strong>Billing:</strong> <span class="text-amber-700 dark:text-amber-400">Pick a billing date — usually rolled forward from last payment</span>`;
      }
    }

    let pausedHtml = "";
    if (this.isCreatePaused()) {
      const resume = this.readValue("pausedUntil");
      const detail = resume
        ? `auto-resumes ${this.formatDate(resume)}`
        : "indefinite (manual resume)";
      pausedHtml = `<p class="text-sm text-amber-700 dark:text-amber-400 mt-1"><strong>State:</strong> Created paused · ${detail}. Stripe billing is paused too.</p>`;
    }

    this.summaryTarget.innerHTML = `
      <p class="text-sm text-gray-700 dark:text-gray-300">${starts}</p>
      <p class="text-sm text-gray-700 dark:text-gray-300 mt-1">${billingHtml}</p>
      ${pausedHtml}
    `;
  }

  isMakeFree() {
    if (!this.hasMakeFreeTarget) return false;
    const input = this.makeFreeTarget.querySelector('input[type="checkbox"]');
    return !!(input && input.checked);
  }

  isCreatePaused() {
    if (!this.hasCreatePausedTarget) return false;
    const input = this.createPausedTarget.querySelector('input[type="checkbox"]');
    return !!(input && input.checked);
  }

  readValue(targetName) {
    const cap = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`;
    if (!this[cap]) return null;
    const v = this[`${targetName}Target`].value;
    return v && v.toString().trim() !== "" ? v : null;
  }

  parseDollarsToCents(str) {
    if (str == null) return null;
    const cleaned = str.toString().replace(/[^0-9.]/g, "");
    if (cleaned === "") return null;
    const f = parseFloat(cleaned);
    if (Number.isNaN(f)) return null;
    return Math.round(f * 100);
  }

  formatMoney(cents) {
    if (cents == null) return "—";
    return `$${(cents / 100).toFixed(2)}`;
  }

  formatInterval(plan) {
    const n = parseInt(plan.interval, 10) || 1;
    const unit = plan.interval_type || "month";
    if (n === 1) return `every ${unit}`;
    return `every ${n} ${unit}s`;
  }

  formatDate(iso) {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }

  // ── Internals ──

  selectedPlanId() {
    return this.hasPlanSelectTarget ? this.planSelectTarget.value : null;
  }

  syncFieldGroups(planId) {
    const plan = this.plansValue[planId];
    const type = plan ? plan.type : null;
    this.toggle("recurringFields", type === "recurring");
    this.toggle("oneTimeFields", type === "one_time");
    this.toggle("creditPackFields", type === "credit_pack");
  }

  suggestBillingDate(plan) {
    if (!this.hasBillingDateTarget) return;
    if (!this.lastPaymentValue) return;
    const interval = parseInt(plan.interval, 10);
    if (!interval || interval <= 0) return;

    const next = this.rollForward(this.lastPaymentValue, interval, plan.interval_type, this.todayValue);
    if (next) {
      this.billingDateTarget.value = next;
      this.billingDateTarget.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  rollForward(startIso, count, type, todayIso) {
    const date = new Date(`${startIso}T00:00:00`);
    const today = new Date(`${todayIso}T00:00:00`);
    let guard = 0;
    while (date < today && guard < 5000) {
      this.advance(date, count, type);
      guard += 1;
    }
    return this.toIso(date);
  }

  advance(date, count, type) {
    switch (type) {
      case "day":   date.setDate(date.getDate() + count); break;
      case "week":  date.setDate(date.getDate() + 7 * count); break;
      case "month": date.setMonth(date.getMonth() + count); break;
      case "year":  date.setFullYear(date.getFullYear() + count); break;
    }
  }

  toIso(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  toggle(targetName, visible) {
    const cap = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`;
    if (!this[cap]) return;
    const el = this[`${targetName}Target`];
    visible ? el.classList.remove("hidden") : el.classList.add("hidden");
  }

  readOscarTarget(name) {
    const cap = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`;
    if (!this[cap]) return null;
    const val = this[`${name}Target`].dataset.value;
    return (val === undefined || val === "" || val === "null") ? null : val;
  }

  setTarget(name, value) {
    const cap = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`;
    if (!this[cap] || value === null || value === undefined) return;
    this[`${name}Target`].value = value;
    this[`${name}Target`].dispatchEvent(new Event("input", { bubbles: true }));
  }
}
